<bk-auth-layout eyebrow="Admin console" [heading]="heading" [sub]="sub">
  <!-- Always present, so "Check the link again" is announced when the text changes -->
  <p class="bk-sr-only" role="status">{{ isVerifyingToken ? "Checking your reset link…" : "" }}</p>

  <!-- Verifying Token State -->
  @if (isVerifyingToken) {
    <div class="bk-auth-step">
      <div class="bk-auth-skel" aria-hidden="true">
        <span class="bk-skel" style="width: 34%; height: 12px"></span>
        <span class="bk-skel" style="height: 48px; border-radius: 12px"></span>
        <span class="bk-skel" style="width: 72%; height: 12px; margin-top: 4px"></span>
        <span class="bk-skel" style="width: 58%; height: 12px"></span>
        <span class="bk-skel" style="height: 52px; margin-top: 14px; border-radius: 999px"></span>
      </div>
    </div>
  } @else if (tokenError) {
    <!-- Token Error State -->
    <div class="bk-auth-step">
      <div class="bk-auth-note" data-tone="bad" role="alert">
        <span class="bk-auth-dot" aria-hidden="true"></span>
        <div class="bk-auth-note__body">
          <p>{{ tokenError }}</p>
        </div>
      </div>

      @if (tokenCheckFailed) {
        <button type="button" class="bk-btn bk-btn--primary bk-auth-cta" (click)="verifyToken()">
          Check the link again
        </button>
        <p class="bk-auth-alt">
          <a class="bk-auth-link" routerLink="/reset-password">Ask for a new link</a>
          <a class="bk-auth-link" routerLink="/">Back to sign in</a>
        </p>
      } @else {
        <a class="bk-btn bk-btn--primary bk-auth-cta" routerLink="/reset-password">Ask for a new link</a>
        <p class="bk-auth-alt">
          <a class="bk-auth-link" routerLink="/">Back to sign in</a>
        </p>
      }
    </div>
  } @else if (isTokenValid && !successful) {
    <!-- Valid Token - Show Password Reset Form -->
    <div class="bk-auth-step">
      @if (feedback) {
        <div class="bk-auth-note" data-tone="bad" role="alert">
          <span class="bk-auth-dot" aria-hidden="true"></span>
          <div class="bk-auth-note__body">
            <p class="bk-auth-note__title">{{ feedback.title }}</p>
            <p class="bk-auth-note__quiet">{{ feedback.message }}</p>
            @if (feedback.offerNewLink) {
              <a class="bk-auth-link" routerLink="/reset-password">Ask for a new link</a>
            }
          </div>
        </div>
      }

      <form
        class="bk-auth-form"
        [formGroup]="newPasswordForm"
        (ngSubmit)="handleSubmit()"
        novalidate
        [attr.aria-busy]="isLoading"
      >
        <!-- new password -->
        <div class="bk-field" [class.is-invalid]="showNewPasswordError">
          <label class="bk-field__label" for="new-password">New password</label>
          <div class="bk-field__wrap">
            <input
              id="new-password"
              class="bk-field__input"
              [type]="showNewPassword ? 'text' : 'password'"
              formControlName="newPassword"
              autocomplete="new-password"
              autocapitalize="none"
              autocorrect="off"
              spellcheck="false"
              [attr.aria-invalid]="showNewPasswordError"
              [attr.aria-describedby]="
                (showNewPasswordError ? 'new-password-error ' : '') + 'new-password-rules new-password-note'
              "
              (keydown)="checkCapsLock($event, 'new')"
              (keyup)="checkCapsLock($event, 'new')"
              (blur)="clearCapsLock('new')"
            />
            <button
              type="button"
              class="bk-field__reveal"
              aria-controls="new-password"
              aria-label="Show password"
              [attr.aria-pressed]="showNewPassword"
              (click)="toggleNewPasswordVisibility()"
            >
              <span class="bk-sym" aria-hidden="true">{{ showNewPassword ? "visibility_off" : "visibility" }}</span>
            </button>
          </div>
          @if (showNewPasswordError) {
            <p class="bk-field__error" id="new-password-error">
              <span class="bk-sym" aria-hidden="true">error</span>{{ newPasswordErrorMessage }}
            </p>
          }
          <div class="bk-auth-fieldfoot">
            <div aria-live="polite">
              @if (capsLockField === "new") {
                <p class="bk-auth-caps">
                  <span class="bk-sym" aria-hidden="true">keyboard_capslock</span>Caps Lock is on
                </p>
              }
            </div>
          </div>

          <!-- Mirrors the server's rules, so a reset attempt isn't wasted.
               No aria-label: the field's description must read the rules
               themselves, with their state. -->
          <ul class="bk-auth-rules" id="new-password-rules">
            @for (rule of rules; track rule.id) {
              <li class="bk-auth-rule" [attr.data-state]="ruleTone(rule)">
                <!-- Shape as well as colour: check, cross, or a grey dot -->
                @switch (ruleTone(rule)) {
                  @case ("ok") {
                    <span class="bk-sym bk-auth-rule__mark" aria-hidden="true">check</span>
                  }
                  @case ("bad") {
                    <span class="bk-sym bk-auth-rule__mark" aria-hidden="true">close</span>
                  }
                  @default {
                    <span class="bk-auth-dot" aria-hidden="true"></span>
                  }
                }
                <span class="bk-auth-rule__text">
                  {{ rule.label }}
                  @if (rule.id === "symbol") {
                    <span class="bk-auth-symbols">{{ symbols }}</span>
                  }
                  @if (rule.meta) {
                    <span class="bk-auth-rule__meta">{{ rule.meta }}</span>
                  }
                  <span class="bk-sr-only">
                    {{ ruleTone(rule) === "ok" ? ", done" : ruleTone(rule) === "bad" ? ", needs fixing" : ", not yet" }}
                  </span>
                  @if (rule.fix) {
                    <span class="bk-auth-rule__fix">{{ rule.fix }}</span>
                  }
                </span>
              </li>
            }
          </ul>
          <p class="bk-sr-only" aria-live="polite">{{ rulesSummary }}</p>
          <p class="bk-auth-rules-note" id="new-password-note">
            It also can’t contain your email address or username, or match a recent password.
          </p>
        </div>

        <!-- confirm password -->
        <div class="bk-field" [class.is-invalid]="showConfirmPasswordError">
          <label class="bk-field__label" for="confirm-password">Confirm new password</label>
          <div class="bk-field__wrap">
            <input
              id="confirm-password"
              class="bk-field__input"
              [type]="showConfirmPassword ? 'text' : 'password'"
              formControlName="confirmPassword"
              autocomplete="new-password"
              autocapitalize="none"
              autocorrect="off"
              spellcheck="false"
              [attr.aria-invalid]="showConfirmPasswordError"
              [attr.aria-describedby]="showConfirmPasswordError ? 'confirm-password-error' : null"
              (keydown)="checkCapsLock($event, 'confirm')"
              (keyup)="checkCapsLock($event, 'confirm')"
              (blur)="clearCapsLock('confirm')"
            />
            <button
              type="button"
              class="bk-field__reveal"
              aria-controls="confirm-password"
              aria-label="Show password"
              [attr.aria-pressed]="showConfirmPassword"
              (click)="toggleConfirmPasswordVisibility()"
            >
              <span class="bk-sym" aria-hidden="true">{{ showConfirmPassword ? "visibility_off" : "visibility" }}</span>
            </button>
          </div>
          @if (showConfirmPasswordError) {
            <p class="bk-field__error" id="confirm-password-error">
              <span class="bk-sym" aria-hidden="true">error</span>{{ confirmPasswordErrorMessage }}
            </p>
          }
          <div class="bk-auth-fieldfoot">
            <div aria-live="polite">
              @if (capsLockField === "confirm") {
                <p class="bk-auth-caps">
                  <span class="bk-sym" aria-hidden="true">keyboard_capslock</span>Caps Lock is on
                </p>
              }
            </div>
          </div>
        </div>

        <!-- aria-disabled keeps focus on the button; handleSubmit ignores presses while saving -->
        <button
          type="submit"
          class="bk-btn bk-btn--primary bk-auth-cta"
          [attr.aria-disabled]="isLoading || null"
          [attr.aria-busy]="isLoading"
        >
          {{ isLoading ? "Saving…" : "Set new password" }}
        </button>
      </form>

      <p class="bk-auth-alt">
        <span>Remembered it? <a class="bk-auth-link" routerLink="/">Back to sign in</a></span>
      </p>
    </div>
  } @else if (successful) {
    <!-- Success State -->
    <div class="bk-auth-step">
      <p class="bk-auth-inline" data-tone="ok" role="status">
        <span class="bk-auth-dot" aria-hidden="true"></span>
        Your password was changed. The reset link no longer works.
      </p>
      <a class="bk-btn bk-btn--primary bk-auth-cta" routerLink="/">Sign in</a>
    </div>
  }
</bk-auth-layout>
